import { useMemo, useState } from "react"
import { Pencil } from "lucide-react"

import { useAuth } from "@/auth/AuthProvider"
import {
  useAdminUsers,
  useCreateUser,
  useDeleteUser,
  useChangeUserRole,
  type AdminUser,
} from "@/api/hooks/admin"
import { showToast } from "@/lib/toast"

import { CreateUserModal } from "./CreateUserModal"
import { ChangeRoleModal } from "./ChangeRoleModal"
import UserAccountsModal from "./UserAccountsModal"
import UserNetworksModal from "./UserNetworksModal"
import SubordinatesModal from "./SubordinatesModal"
import { networkLabel } from "@/lib/networkAccess"

const ROLE_COLORS: Record<string, string> = {
  // Solid-fill pills matching the legacy Jinja palette.
  admin: "bg-yellow-400 text-[#1a1a1a]",
  mediabuyer: "bg-cyan-500 text-[#1a1a1a]",
  team_leader: "bg-emerald-500 text-[#1a1a1a]",
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_COLORS[role] ?? "bg-gray-700 text-gray-300"}`}
    >
      {role}
    </span>
  )
}

function InlineEditButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-[#a0a0a0] transition-colors hover:bg-[#505050] hover:text-white"
      aria-label={label}
      title={label}
    >
      <Pencil className="h-3.5 w-3.5" />
    </button>
  )
}

export default function UsersTab() {
  const { user: currentUser } = useAuth()
  const currentAdminId = currentUser?.id

  const { data, isLoading, isError, error } = useAdminUsers()
  const createUser = useCreateUser()
  const deleteUser = useDeleteUser()
  const changeRole = useChangeUserRole()

  const [createOpen, setCreateOpen] = useState(false)
  const [roleModalUser, setRoleModalUser] = useState<AdminUser | null>(null)
  const [accountsModalUser, setAccountsModalUser] = useState<AdminUser | null>(
    null,
  )
  const [networksModalUser, setNetworksModalUser] = useState<AdminUser | null>(
    null,
  )
  const [subordinatesUser, setSubordinatesUser] = useState<AdminUser | null>(
    null,
  )
  const [search, setSearch] = useState("")

  const users: AdminUser[] = useMemo(() => data?.data ?? [], [data])
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter((user) => {
      const networks = (user.network_slugs ?? [])
        .map((slug) => networkLabel(slug).toLowerCase())
        .join(" ")
      return (
        String(user.id).includes(q) ||
        user.username.toLowerCase().includes(q) ||
        user.email.toLowerCase().includes(q) ||
        user.role.toLowerCase().includes(q) ||
        networks.includes(q)
      )
    })
  }, [users, search])

  function handleCreateUser(data: { username: string; email: string; password: string; role: string }) {
    createUser.mutate(data, {
      onSuccess: () => {
        showToast("User created successfully")
        setCreateOpen(false)
      },
      onError: (err) => showToast(err instanceof Error ? err.message : "Error creating user", "error"),
    })
  }

  function handleDeleteUser(user: AdminUser) {
    if (user.id === currentAdminId) return
    if (
      !window.confirm(
        `Are you sure you want to delete ${user.username}? This action cannot be undone.`,
      )
    )
      return
    deleteUser.mutate(user.id, {
      onSuccess: () => showToast(`User ${user.username} deleted successfully`),
      onError: (err) =>
        showToast(
          err instanceof Error ? err.message : "Error deleting user",
          "error",
        ),
    })
  }

  function handleChangeRole(userId: number, newRole: string) {
    changeRole.mutate({ userId, role: newRole }, {
      onSuccess: () => {
        showToast(`User role updated`)
        setRoleModalUser(null)
      },
      onError: (err) => showToast(err instanceof Error ? err.message : "Error changing role", "error"),
    })
  }

  if (isLoading) {
    return <div className="py-8 text-center text-white">Loading users...</div>
  }

  if (isError) {
    const msg =
      error instanceof Error ? error.message : "Error loading users"
    return (
      <div className="py-8 text-center text-red-400">{msg}</div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username, email, role, ID…"
          aria-label="Search users"
          className="min-w-[200px] flex-1 rounded-lg border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white placeholder:text-[#888] outline-none focus:border-[#8b9aff]"
        />
        <button
          className="shrink-0 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
          onClick={() => setCreateOpen(true)}
        >
          + Create User
        </button>
      </div>

      {users.length === 0 ? (
        <div className="py-8 text-center text-white">No users found</div>
      ) : filteredUsers.length === 0 ? (
        <div className="py-8 text-center text-white">
          No users match “{search.trim()}”
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#4a4a4a]">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="sticky top-0 z-10 bg-[#444444] text-white">
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold">Username</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Networks</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-[#4a4a4a] transition-colors hover:bg-[#444444]"
                >
                  <td className="px-4 py-3 text-white">{user.id}</td>
                  <td className="px-4 py-3 font-medium text-white">
                    {user.username}
                  </td>
                  <td className="px-4 py-3 text-white">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      <RoleBadge role={user.role} />
                      <InlineEditButton
                        label="Change role"
                        onClick={() => setRoleModalUser(user)}
                      />
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {(user.network_slugs ?? ["roots"]).map((slug) => (
                        <span
                          key={slug}
                          className="inline-flex items-center rounded-full border border-[#676d78]/50 bg-[#4f5561]/40 px-2 py-0.5 text-xs font-semibold text-[#d8d8d8]"
                        >
                          {networkLabel(slug)}
                        </span>
                      ))}
                      <InlineEditButton
                        label="Assign networks"
                        onClick={() => setNetworksModalUser(user)}
                      />
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {user.role === "team_leader" && (
                        <button
                          className="rounded bg-purple-700/60 px-2.5 py-1 text-xs font-semibold text-purple-200 transition-colors hover:bg-purple-700"
                          onClick={() => setSubordinatesUser(user)}
                        >
                          Subordinates
                        </button>
                      )}
                      <button
                        className="rounded-md border border-[#676d78] bg-[#4f5561] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#5a616f]"
                        onClick={() => setAccountsModalUser(user)}
                      >
                        Accounts
                      </button>
                      {user.id !== currentAdminId && (
                        <button
                          className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                          onClick={() => handleDeleteUser(user)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreateUser}
        isPending={createUser.isPending}
      />

      <ChangeRoleModal
        user={roleModalUser}
        onClose={() => setRoleModalUser(null)}
        onSubmit={handleChangeRole}
        isPending={changeRole.isPending}
      />

      {accountsModalUser && (
        <UserAccountsModal
          user={accountsModalUser}
          onClose={() => setAccountsModalUser(null)}
        />
      )}

      {networksModalUser && (
        <UserNetworksModal
          user={networksModalUser}
          onClose={() => setNetworksModalUser(null)}
        />
      )}

      {subordinatesUser && (
        <SubordinatesModal
          user={subordinatesUser}
          onClose={() => setSubordinatesUser(null)}
        />
      )}
    </div>
  )
}
